import React, { memo, useMemo, useRef, useCallback, StrictMode, useEffect, useState } from 'react';
import { useEvent } from '../../../../../../hooks/useEvent';
import { GridData, GridDataPagedResponse, GridHeaderRow } from '../../../../../../api/generated/data-contracts';
import { AllRowsClosedProvider } from '../../../../_grid/contexts/allRowsClosedContext';
import { ExpandedGridProvider } from '../../../../_grid/contexts/expandedContext';
import { GridContext, GridContextProvider } from '../../../../_grid/contexts/gridContext';
import { RowUpdateInfo, UseGridProps, useGrid } from '../../../../_grid/useGrid';
import { useLocalStorage } from '../../../../../../hooks/useLocalStorage';
import { ScrollToContextProvider, ScrollToHandler } from '../context/ScrollToContext';
import {
    COLUMN_STICKY_COUNT,
    COLUMN_WIDTH,
    ROW_HEADER_WIDTH,
    ROW_HEIGHT,
    ROW_STICKY_COUNT,
    TOP_HEADERS_HEIGHT,
    TOTAL_COLUMN_WIDTH,
} from '../CONSTANTS';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { getRowType } from '../helpers/getRowType';
import { useDebouncedEventHandler } from '../../../../../../hooks/useDebouncedHandler';
import { useGridDragHandler } from '../hooks/useGridDragHandler';
import { DragContextProvider } from '../context/dragContext';
import { GridLayoutContextProvider, useGridLayoutContext } from '../context/LayoutContext';
import { HeaderContainer } from './HeaderContainer';
import { RowContainer } from './RowContainer/RowContainer';
import { useAllocationData } from '../../../hooks/useAllocationData';
import { useUnitType } from '../../../../../../Utilities/UnitTypeUtil';
import { ShimmerBox } from '../../../../../../_components/Skeleton/ShimmerBox';
import { Stack } from '@fluentui/react';
import shallow from 'zustand/shallow';
import { useStableVirtualItems } from '../../../../_grid/hooks/useStableVirtualItems';
import { InfiniteData, useQuery } from '@tanstack/react-query';
import { ALLOCATION_VIEW, ALLOCATION_VIEW_TOP_ROW } from '../../../../../../context/network/http/QueryProvider/queryKeys';
import { useMemoQueryKey } from '../../../../../../context/network/http/QueryProvider/queries/useMemoQueryKey';
import { useStore } from '../../../../../../context/store';
import produce from 'immer';
import { useUpdateOnUnmount } from '../../../../../../context/network/http/QueryProvider/queries/useUpdateOnUnmount';
import { GroupingContextProvider } from '../context/GroupingContext';
import { DragBox } from './DragBox';
import { useLocation, useParams } from 'react-router';
import { ApiCalls } from '../../../../../../api/api';
import { TopTotalRow } from './RowContainer/components/toptotal/TopTotalRow';
import { useRecentlyViewedContext } from '../../../../../../hooks/useRecentlyViewedContext';

type AllocationGridProps = Omit<UseGridProps, 'data' | 'onUpdateCellTree'>;

const overscan = 5;
const loadingName = 'Loading..'

export const AllocationGrid = memo(function AllocationGrid({ readonly, ...rest }: AllocationGridProps) {
    const [chunkSize, setChunkSize] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const { currentUnitType } = useUnitType();
    const { timelineStart, timelineEnd, timelineResolution } = useStore(store => {
        return {
            timelineEnd: store.timeline.end,
            timelineStart: store.timeline.start,
            timelineResolution: store.timeline.resolution,
        };
    }, shallow);

    const [grouping, setGrouping] = useLocalStorage<string | null>('tp-allocation-groupby', null);

    const { projectId } = useParams<{ projectId: string }>();
    const commonQueryKey = useMemoQueryKey(timelineStart, timelineEnd, timelineResolution, currentUnitType, projectId);
    const gridQueryKey = useMemoQueryKey(ALLOCATION_VIEW, grouping, ...commonQueryKey);

    const {
        data: pagedData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useAllocationData({
        chunkSize,
        overscan,
        queryKey: gridQueryKey,
        timelineEnd,
        timelineStart,
        timelineResolution,
        grouping,
        projectId,
    });

    const [prevHeaders, setPrevHeaders] = useState<GridHeaderRow[]>([]);

    const data = useMemo((): GridData => {
        if (pagedData?.pages.length) {
            return {
                ...pagedData.pages[0].data,
                rows: pagedData.pages.flatMap(d => d.data.rows),
                // name: "Hello"
            };
        }
        return {
            headers: prevHeaders,
            id: '123',
            rows: [],
            metadata: null,
            name: loadingName,
        };
    }, [pagedData?.pages, prevHeaders]);

	const { pathname } = useLocation();

	useRecentlyViewedContext({
		text: data.name !== loadingName ? data.name : null,
		url: pathname,
		viewName: "allocation",
		enabled: Boolean(projectId)
	})

    const topRowQueryKey = useMemoQueryKey(ALLOCATION_VIEW_TOP_ROW, ...commonQueryKey);

    const {
        data: topTotalRow,
        refetch,
        isFetching,
    } = useQuery({
        queryKey: topRowQueryKey,
        queryFn: async ({ signal }) => {
			if (!signal.aborted) {
				return ApiCalls.getAllocationTotalRow(
					{
						dateResolution: timelineResolution,
						end: timelineEnd,
						projectId,
						start: timelineStart,
					},
					{ signal },
				).then(res => res.data);
			}
        },
    });

    const onUpdateCellTree = useEvent(({ diff, row, totalRows, setCellValue, columnIndex, cellIndex }: RowUpdateInfo) => {
        const freeCap = row.columnRows[columnIndex].cells[2];
        setCellValue(freeCap, value => value.minus(diff));
        const totalColumn = row.totalColumn;
        const allocationTotalCell = totalColumn.cells[cellIndex];
        setCellValue(allocationTotalCell, value => value.plus(diff));
        for (const totalRow of totalRows) {
            const allocationTotalCell = totalRow.totalColumn.cells[cellIndex];
            setCellValue(allocationTotalCell, value => value.plus(diff));
        }
        // Update the totalRopRow if we have it
        if (topTotalRow) {
            const totalCell = topTotalRow.columnRows[columnIndex].cells[cellIndex];
            setCellValue(totalCell, value => value.plus(diff));
            const totalColumnCell = topTotalRow.totalColumn.cells[cellIndex];
            setCellValue(totalColumnCell, value => value.plus(diff));

            // This function runs on every cell update.
            // We cannot trust the data that is currently being fetched because we just changed the data.
            // Refetching it cancels the current response and queues a new response with updated totals.
        } else if (isFetching) {
            console.log('refetching');
            refetch();
        }
    });

    const {
        gridData: { visibleRows, maxGridDepth, allClosed },
        gridMethods: {
            getRowAncestorTreeToRoot,
            getSubRows,
            updateCellValue,
            addRow,
            useGridCellState,
            useGridCellValue,
            getUpdatedData,
            moveRow,
            getRowsMap,
            setRootRows: setRows,
        },
        staticGridData: { headers, name, metadata, id },
        expandedRows,
        setExpandedRows,
    } = useGrid({
        ...rest,
        data,
        onUpdateCellTree,
    });

    useEffect(() => {
        if (headers) {
            setPrevHeaders(headers);
        }
    }, [headers]);

    useUpdateOnUnmount(gridQueryKey, getUpdatedData, (newData, queryClient) => {
        const cachedData = queryClient.getQueryData<InfiniteData<GridDataPagedResponse> | void>(gridQueryKey);
        if (cachedData) {
            const cellMap = getCellsMap(newData);
            const updatedData = produce(cachedData, cachedData => {
                cachedData.pages.forEach(({ data: { rows } }) => {
                    rows.forEach(row => {
                        // const newMetaData = newData.rows.find(newRow => newRow.id === row.id)?.metadata;
                        // if (newMetaData) {
                        //     row.metadata = newMetaData;
                        // }
                        row.columnRows.forEach(columnRow => {
                            columnRow.cells = columnRow.cells.map(cell => {
                                if (cell) {
                                    const newCell = cellMap.get(cell.id);
                                    if (newCell) {
                                        return newCell;
                                    }
                                }
                                return cell;
                            });
                        });
                    });
                });
            });
            queryClient.setQueryData<InfiniteData<GridDataPagedResponse>>(gridQueryKey, () => updatedData);
        }
    });

    const context = useMemo((): GridContext => {
        return {
            name,
            headers,
            properties: metadata,
            id,
            readonly,
            maxGridDepth,
            updateCellValue,
            getRowAncestorTreeToRoot,
            addRow,
            useGridCellState,
            useGridCellValue,
            moveRow,
            getRowsMap,
            setRootRows: setRows,
        };
    }, [
        name,
        headers,
        metadata,
        id,
        readonly,
        maxGridDepth,
        updateCellValue,
        getRowAncestorTreeToRoot,
        addRow,
        useGridCellState,
        useGridCellValue,
        moveRow,
        getRowsMap,
        setRows,
    ]);

    const expandedContext = useMemo(() => {
        return {
            expandedRows,
            setExpandedRows,
        };
    }, [expandedRows, setExpandedRows]);

    const renderableRows = useMemo(() => {
        return visibleRows.filter(({ visible }) => visible);
    }, [visibleRows]);

    const activeRowStickyIndexRef = useRef(0);

    const stickyColumnIndexes = useMemo(() => Array.from({ length: COLUMN_STICKY_COUNT }, (_, i) => i), []);
    const stickyRowIndexes = useMemo(() => [0], []);

    const pendingCellIndexScroll = useRef<{ dir: 'x' | 'y'; index: number } | null>(null);

    const [rowHeaderSize, _setRowHeaderSize] = useLocalStorage('row-header-width', ROW_HEADER_WIDTH);
    const [hidePersonaIcons, setHidePersonaIcons] = useLocalStorage('hide-persona-icons', false);

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: (visibleRows?.[0]?.row?.columnRows?.length || prevHeaders?.length || 0) + COLUMN_STICKY_COUNT,
        getScrollElement: () => scrollRef.current,
        overscan,
        estimateSize: useCallback(
            i => {
                // First columns is the row headers
                if (i === 0) {
                    return rowHeaderSize;
                }
                if (i === 1) {
                    return TOTAL_COLUMN_WIDTH;
                }
                return COLUMN_WIDTH;
            },
            [rowHeaderSize],
        ),
        rangeExtractor: useCallback(
            range => {
                const next = new Set([...stickyColumnIndexes, ...defaultRangeExtractor(range)]);
                return [...next].sort((a, b) => a - b);
            },
            [stickyColumnIndexes],
        ),
        onChange: useEvent((instance, sync) => {
            if (!sync) {
                if (pendingCellIndexScroll.current !== null) {
                    const { dir, index } = pendingCellIndexScroll.current;
                    if (dir === 'y') {
                        rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
                        pendingCellIndexScroll.current = null;
                    }
                }
            }
        }),
    });

    const rowCount = useMemo(() => {
        if (!pagedData) {
            return ROW_STICKY_COUNT + chunkSize;
        }
        if (hasNextPage) {
            return renderableRows.length + ROW_STICKY_COUNT + 1;
        }
        return renderableRows.length + ROW_STICKY_COUNT;
    }, [chunkSize, hasNextPage, pagedData, renderableRows.length]);

    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        overscan,
        estimateSize: useCallback(
            (index: number) => {
                if (index === 0) {
                    return TOP_HEADERS_HEIGHT;
                }
                // This is the top total row
                if (index === 1) {
                    return ROW_HEIGHT;
                }

                const visibleRow = renderableRows[index - ROW_STICKY_COUNT];
                if (!visibleRow) {
                    return ROW_HEIGHT;
                }
                const { expanded, row } = visibleRow;
                if (expanded) {
                    const rowType = getRowType(row);
                    switch (rowType) {
                        // return ROW_EXPANDED_HEIGHT;
                        case 'resource':
                        case 'project':
                        case 'contract':
                        default:
                            return ROW_HEIGHT;
                    }
                }
                return ROW_HEIGHT;
            },
            [renderableRows],
        ),
        rangeExtractor: useCallback(
            range => {
                activeRowStickyIndexRef.current = [...stickyRowIndexes].reverse().find(index => range.startIndex >= index);

                const next = new Set([activeRowStickyIndexRef.current, ...defaultRangeExtractor(range)]);

                return [...next].sort((a, b) => a - b);
            },
            [stickyRowIndexes],
        ),
        onChange: useEvent((instance, sync) => {
            if (!sync) {
                if (pendingCellIndexScroll.current !== null) {
                    const { dir, index } = pendingCellIndexScroll.current;
                    if (dir === 'x') {
                        columnVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
                        pendingCellIndexScroll.current = null;
                    }
                }
            }
        }),
    });

    useEffect(() => {
        if (rowVirtualizer.scrollRect?.height && chunkSize === 0) {
            setChunkSize(Math.floor(rowVirtualizer.scrollRect.height / ROW_HEIGHT) || 10);
        }
    }, [rowVirtualizer.scrollRect?.height, chunkSize]);

    const rows = useStableVirtualItems(rowVirtualizer.getVirtualItems());

    useEffect(() => {
        if (!pagedData) {
            return;
        }

        const lastItem = rows.at(-1);

        if (!lastItem) {
            return;
        }

        if (renderableRows.length && lastItem.index >= renderableRows.length - 3 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, renderableRows.length, isFetchingNextPage, rows, pagedData]);

    const setRowHeaderSize = useDebouncedEventHandler<React.Dispatch<React.SetStateAction<number>>>(
        () => {
            columnVirtualizer.measure();
        },
        arg => {
            _setRowHeaderSize(arg);
        },
        20,
    );

    const layoutValue = useMemo(
        () => ({ rowHeaderSize, setRowHeaderSize, hidePersonaIcons, setHidePersonaIcons }),
        [rowHeaderSize, setRowHeaderSize, hidePersonaIcons, setHidePersonaIcons],
    );

    const scrollTo = useDebouncedEventHandler<ScrollToHandler>(({ x, y, rowIndex, columnIndex, behavior = 'smooth', align = 'center' }) => {
        const scrollToIndex = (columnIndex?: number, rowIndex?: number) => {
            if (columnIndex !== undefined && rowIndex !== undefined) {
                rowVirtualizer.scrollToIndex(rowIndex, { align, behavior });
                pendingCellIndexScroll.current = { dir: 'x', index: columnIndex };
            }
            if (columnIndex !== undefined) {
                columnVirtualizer.scrollToIndex(columnIndex, { align, behavior });
            }
            if (rowIndex !== undefined) {
                rowVirtualizer.scrollToIndex(rowIndex, { align, behavior });
            }
        };
        if (x !== undefined && y !== undefined) {
            const colIndex = columnVirtualizer.getVirtualItemForOffset(x).index;
            const rowIndex = rowVirtualizer.getVirtualItemForOffset(y).index;
            scrollToIndex(colIndex, rowIndex);
            return;
        }
        if (x !== undefined) {
            const colIndex = columnVirtualizer.getVirtualItemForOffset(x).index;
            scrollToIndex(colIndex);
            return;
        }
        if (y !== undefined) {
            const rowIndex = rowVirtualizer.getVirtualItemForOffset(x).index;
            scrollToIndex(undefined, rowIndex);
            return;
        }
        scrollToIndex(columnIndex, rowIndex);
    }, 20);

    const columns = useStableVirtualItems(columnVirtualizer.getVirtualItems());
    const totalWidth = columnVirtualizer.getTotalSize();
    const totalHeight = rowVirtualizer.getTotalSize();

    const renderedRows = useMemo(() => {
        return rows.map(({ index, start, key }) => {
            if (index === 0) {
                return <HeaderContainer key={String(key)} headers={headers} start={start} totalWidth={totalWidth} columns={columns} gridName={data.name} />;
            }
            if (index === 1) {
                return (
                    <TopTotalRow
                        key={String(key)}
                        start={start}
                        totalWidth={totalWidth}
                        setTotalReady={() => {}}
                        columnCount={headers.length}
                        columns={columns}
                        row={topTotalRow}
                    />
                );
            }
            const rowIndex = index - ROW_STICKY_COUNT;
            const visibleRow = renderableRows[rowIndex];
            if (!visibleRow) {
                return <ShimmerRow key={String(key)} start={start} totalWidth={totalWidth} columnCount={headers.length} />;
            }
            const { row, visible, nestingLevel, expanded, rowRelations } = visibleRow;
            return (
                <RowContainer
                    key={String(key)}
                    row={row}
                    setExpandedRows={setExpandedRows}
                    visible={visible}
                    expanded={expanded}
                    getRowAncestorTreeToRoot={getRowAncestorTreeToRoot}
                    getSubRows={getSubRows}
                    nestingLevel={nestingLevel}
                    maxGridDepth={maxGridDepth}
                    rowIndex={rowIndex}
                    rowRelations={rowRelations}
                    start={start}
                    columns={columns}
                    totalWidth={totalWidth}
                />
            );
        });
    }, [rows, renderableRows, setExpandedRows, getRowAncestorTreeToRoot, getSubRows, maxGridDepth, columns, totalWidth, headers, data.name, topTotalRow]);

    const dragContext = useGridDragHandler({
        getVirtualColumnItemForOffset: useEvent(columnVirtualizer.getVirtualItemForOffset),
        getVirtualRowItemForOffset: useEvent(rowVirtualizer.getVirtualItemForOffset),
        columnScrollOffset: columnVirtualizer.scrollOffset,
        rowScrollOffset: rowVirtualizer.scrollOffset,
        renderableRows,
        scrollRef,
        updateCellValue,
    });

    const innerScrollContainerStyles = useMemo(
        (): React.CSSProperties => ({
            position: 'relative',
            width: '100%',
            height: `${totalHeight}px`,
        }),
        [totalHeight],
    );

    return (
        <StrictMode>
            <ScrollToContextProvider value={scrollTo}>
                <GridLayoutContextProvider value={layoutValue}>
                    <DragContextProvider value={dragContext}>
                        <GridContextProvider value={context}>
                            <ExpandedGridProvider value={expandedContext}>
                                <AllRowsClosedProvider value={allClosed}>
                                    <GroupingContextProvider grouping={grouping} setGrouping={setGrouping}>
                                        <div ref={scrollRef} style={scrollContainerStyle}>
                                            <div style={innerScrollContainerStyles}>{renderedRows}</div>
                                        </div>
                                        <DragBox />
                                    </GroupingContextProvider>
                                </AllRowsClosedProvider>
                            </ExpandedGridProvider>
                        </GridContextProvider>
                    </DragContextProvider>
                </GridLayoutContextProvider>
            </ScrollToContextProvider>
        </StrictMode>
    );
});

const scrollContainerStyle: React.CSSProperties = {
    height: `100%`,
    width: `100%`,
    overflow: 'auto',
    maxWidth: '100%',
};

const ShimmerRow = ({ start, totalWidth, columnCount }: { start: number; totalWidth: number; columnCount: number }) => {
    const { rowHeaderSize } = useGridLayoutContext();
    const style = useMemo((): React.CSSProperties => {
        return {
            position: 'absolute',
            transform: `translateY(${start}px)`,
            top: 0,
            left: 0,
            height: `${ROW_HEIGHT}px`,
            width: `${totalWidth}px`,
            boxSizing: 'border-box',
            display: 'flex',
        };
    }, [start, totalWidth]);
    const columns = useMemo(() => {
        return [
            <Stack key={-1} verticalAlign="center" horizontalAlign="center" styles={{ root: { width: TOTAL_COLUMN_WIDTH } }}>
                <ShimmerBox height={ROW_HEIGHT - 10} width={TOTAL_COLUMN_WIDTH - 10} />
            </Stack>,
            ...Array.from({ length: columnCount }, (_, i) => {
                return (
                    <Stack key={i} verticalAlign="center" horizontalAlign="center" styles={{ root: { width: COLUMN_WIDTH } }}>
                        <ShimmerBox height={ROW_HEIGHT - 10} width={COLUMN_WIDTH - 10} />
                    </Stack>
                );
            }),
        ];
    }, [columnCount]);
    return (
        <div style={style}>
            <Stack verticalAlign="center" horizontalAlign="center" styles={{ root: { width: rowHeaderSize } }}>
                <ShimmerBox height={ROW_HEIGHT - 10} width={rowHeaderSize - 10} />
            </Stack>
            {columns}
        </div>
    );
};

const getCellsMap = (data: GridData) => {
    const allCells = data.rows.flatMap(row => row.columnRows.flatMap(columnRow => columnRow.cells)).filter(Boolean);
    return new Map(allCells.map(cell => [cell.id, cell]));
};
